/**
 * What the settings modal edits before Save (TV-17): numbers stay the text
 * typed in their fields until they are checked.
 */
import { INPUT_TYPE, PLOT_TYPE } from "../../engine/indicators/indicatorTypes";

const DECIMAL_COMMA = ",";
const DECIMAL_POINT = ".";

/**
 * @param {string|number} text
 * @param {number} fallback - when the text is not a number
 * @returns {number}
 */
const parseNumber = (text, fallback) => {
  const trimmed = String(text).trim().replace(DECIMAL_COMMA, DECIMAL_POINT);
  const number = Number(trimmed);

  return trimmed && Number.isFinite(number) ? number : fallback;
};

/**
 * A number input as the engine keeps it (normalizeInputs): not below its
 * minimum, whole when it must be; the previous value when the text is not a number.
 *
 * @param {object} input - registry input
 * @param {string|number} text
 * @param {number} fallback
 * @returns {number}
 */
export const normalizeNumberInput = (input, text, fallback) => {
  const bounded = Math.max(input.min, parseNumber(text, fallback));

  return input.isInteger ? Math.round(bounded) : bounded;
};

/** Whether the Styles tab gives a plot a width: lines and points, not columns or backgrounds. */
export const hasPlotWidth = (plot) => plot.type !== PLOT_TYPE.HISTOGRAM && plot.type !== PLOT_TYPE.FILL;

/**
 * @param {{ inputs: object, styles: { plots: object, levels: object[] } }} indicator - engine.getIndicators() item
 * @param {object} definition - from the registry
 * @returns {{ inputs: object, plots: object, levels: object[] }}
 */
export const createDraft = (indicator, definition) => ({
  inputs: Object.fromEntries(
    definition.inputs.map(({ key, type }) => {
      const value = indicator.inputs[key];

      return [key, type === INPUT_TYPE.NUMBER ? String(value) : value];
    }),
  ),
  plots: Object.fromEntries(
    Object.entries(indicator.styles.plots).map(([key, style]) => [key, { ...style }]),
  ),
  levels: indicator.styles.levels.map((level) => ({ ...level, value: String(level.value) })),
});

/**
 * Changes for engine.updateIndicator: every input and style of the draft;
 * a field that is not a number keeps the saved value.
 *
 * @param {object} draft - createDraft
 * @param {object} indicator - engine.getIndicators() item
 * @param {object} definition
 * @returns {{ inputs: object, styles: { plots: object, levels: object[] } }}
 */
export const draftToChanges = (draft, indicator, definition) => ({
  inputs: Object.fromEntries(
    definition.inputs.map((input) => {
      const value = draft.inputs[input.key];
      const saved = indicator.inputs[input.key];

      return [input.key, input.type === INPUT_TYPE.NUMBER ? normalizeNumberInput(input, value, saved) : value];
    }),
  ),
  styles: {
    plots: draft.plots,
    levels: draft.levels.map((level, index) => ({
      ...level,
      value: parseNumber(level.value, indicator.styles.levels[index].value),
    })),
  },
});
